import React from 'react';
import {
  Container, Paper, Typography, Box, Button, Accordion, AccordionSummary, AccordionDetails, Alert, CircularProgress, Divider, FormControlLabel, Checkbox, FormHelperText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';

import theme from '../../../theme/theme';
import DraftSaveButton from '../../../components/DraftSaveButton';
import { DRAFT_KEYS } from '../../../utils/draftUtils';

import { usePreOpOrders } from './hooks/usePreOpOrders';
import PatientAndSurgeryDetails from './screens/PatientAndSurgeryDetails';
import ConsentOrders from './screens/ConsentOrders';
import BloodProductOrders from './screens/BloodProductOrders';
import AntibioticAndMedications from './screens/AntibioticAndMedications';
import { Anticoagulation, Investigations, SurgeonInstructions } from './screens/RemainingSections';

const PreoperativeOrders = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    formData,
    setFormData,
    error,
    success,
    loading,
    emailLoading,
    emailSuccess,
    setEmailSuccess,
    validationErrors,
    handleChange,
    handleBlur,
    handleBloodOrderChange,
    handleSubmit,
    mutationLoading
  } = usePreOpOrders(uhid_no, navigate);

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              component={Link}
              to={`/mainPage/${uhid_no}`}
              state={{ 
                from: 'PreOperativePhase', 
                status: 'success', 
                step: 1,
                page: location.state?.page ?? 0,
                activeMenu: location.state?.activeMenu ?? 'Patients'
              }}
              sx={{ color: '#00695c', textTransform: 'none', fontSize: '1rem' }}
            >
              Back
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography variant="h6" sx={{ color: '#00695c' }}>OTMS</Typography>
            </Box>
          </Box>

          <Typography variant="h4" sx={{ color: '#00695c', mb: 2, fontWeight: 'bold' }}>
            PRE OPERATIVE ORDERS
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight="bold">Form Error:</Typography>
              {error}
            </Alert>
          )}

          {emailSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight="bold">Notification:</Typography>
              {emailSuccess}
            </Alert>
          )}

          {success === "success" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight="bold">Existing Record:</Typography>
              You are updating an existing preoperative order. Any changes will overwrite the previous version.
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Patient & Surgery Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <PatientAndSurgeryDetails 
                    formData={formData} 
                    setFormData={setFormData}
                    handleChange={handleChange} 
                    handleBlur={handleBlur} 
                    validationErrors={validationErrors} 
                />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Consent Orders</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ConsentOrders 
                    formData={formData} 
                    setFormData={setFormData} 
                    handleChange={handleChange} 
                    validationErrors={validationErrors} 
                />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Blood & Product Orders</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <BloodProductOrders 
                    formData={formData} 
                    setFormData={setFormData}
                    handleChange={handleChange} 
                    handleBloodOrderChange={handleBloodOrderChange} 
                    validationErrors={validationErrors} 
                />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Antibiotic & Medications</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <AntibioticAndMedications 
                    formData={formData} 
                    setFormData={setFormData}
                    handleChange={handleChange} 
                />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Anticoagulation</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Anticoagulation 
                    formData={formData} 
                    setFormData={setFormData}
                    handleChange={handleChange} 
                    validationErrors={validationErrors} 
                />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Investigations</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Investigations 
                    formData={formData} 
                    setFormData={setFormData}
                    handleChange={handleChange} 
                />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Surgeon Instructions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SurgeonInstructions 
                    formData={formData} 
                    setFormData={setFormData}
                    handleChange={handleChange} 
                    validationErrors={validationErrors} 
                />
              </AccordionDetails>
            </Accordion>

            <Box sx={{ mt: 3, mb: 2 }}>
              <FormControlLabel
                control={<Checkbox name="finalized" checked={formData.finalized} onChange={handleChange} />}
                label="Confirm Orders as Finalized"
              />
              <FormHelperText>
                Please review all details carefully before finalizing the orders
              </FormHelperText>

              {!formData.finalized && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Orders Not Finalized:</strong> You must confirm the orders as finalized before submission.
                  </Typography>
                </Alert>
              )}

              {formData.finalized && Object.keys(validationErrors).some(key => validationErrors[key]) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Cannot Submit:</strong> Please fix all validation errors before finalizing submission.
                  </Typography>
                </Alert>
              )}

              {formData.finalized && !Object.keys(validationErrors).some(key => validationErrors[key]) && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Ready to Submit:</strong> All validations passed. Orders are ready for submission.
                  </Typography>
                </Alert>
              )}
            </Box>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ marginTop: 2, py: 1.5 }}
              fullWidth
              disabled={mutationLoading || emailLoading || !formData.finalized}
              startIcon={mutationLoading || emailLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {mutationLoading
                ? 'Processing...'
                : success === "success"
                  ? 'Update Orders & Notify Nurses'
                  : 'Submit Orders & Notify Nurses'
              }
            </Button>

            {emailLoading && (
              <Alert severity="info" sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="body1" fontWeight="bold">Processing Email Notification</Typography>
                    <Typography variant="body2">
                      Sending notification to nursing staff and relevant departments...
                    </Typography>
                  </Box>
                </Box>
              </Alert>
            )}

            {mutationLoading && (
              <Alert severity="info" sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="body1" fontWeight="bold">Saving Orders</Typography>
                    <Typography variant="body2">
                      {success === "success"
                        ? "Updating preoperative orders in the system..."
                        : "Creating new preoperative orders in the system..."
                      }
                    </Typography>
                  </Box>
                </Box>
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 2 }}>
              <Alert severity="info" variant="outlined">
                <Typography variant="body2">
                  <strong>Note:</strong> All form data is automatically saved as a draft every 2 seconds.
                  You can safely close and return to continue editing.
                </Typography>
              </Alert>
            </Box>

            <DraftSaveButton
                formKey={DRAFT_KEYS.PREOPERATIVE_ORDERS}
                uhidNo={uhid_no}
                formData={formData}
                onDraftLoaded={(draftData) => {
                  setFormData(prev => ({ ...prev, ...draftData }));
                  setEmailSuccess('Draft loaded successfully! You can continue editing from where you left off.');
                }}
                variant="outlined"
                size="large"
                isUpdate={success === 'success'}
            />
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default PreoperativeOrders;
